import React from 'react';
import moment from 'moment/moment';
import { DateFormats } from 'constants/common';
import { grey, lightGreen, orange } from '@mui/material/colors';
import { Box, Typography, Stack, Avatar, Alert, Divider } from '@mui/material';

import { LastReportViewModel } from '../api';
import { GenerateReportBtn } from './GenerateReportBtn';
import { GenerateMockReportBtn } from './GenerateMockReportBtn';

interface Props {
  voId: string;
  isSuperAdmin: boolean;
  generateReportTooltip: string;
  viewModel: LastReportViewModel;
  isGenerateReportAvailable: boolean;
}

export const ReportInfo = ({
  voId,
  viewModel,
  isSuperAdmin,
  generateReportTooltip,
  isGenerateReportAvailable,
}: Props) => {
  if (!viewModel.view) return null;

  return (
    <Stack gap="0.5rem" height="100%">
      <Divider variant="fullWidth" />

      <Stack gap="0.5rem" flex={1}>
        <Stack flexDirection="row" gap="1rem" alignItems="center" width="100%">
          <Avatar
            sx={{ bgcolor: viewModel.view.status === 'active' ? lightGreen[500] : orange[500] }}
            variant="rounded"
          >
            {viewModel.view.entity.initials}
          </Avatar>

          <Box flex={1}>
            <Typography fontWeight="bold">{viewModel.view.entity.name}</Typography>

            <Typography color="textSecondary">
              Filing #: {viewModel.view.entity.filingNumber} · {viewModel.view.entity.sosLabel}
            </Typography>
          </Box>

          <Box
            sx={{
              justifySelf: 'flex-end',
              borderRadius: '0.5rem',
              padding: '0.2rem 0.5rem',
              backgroundColor: viewModel.view.badge.variant === 'active' ? lightGreen[50] : orange[50],
            }}
          >
            <Typography color={viewModel.view.badge.variant === 'active' ? 'success' : 'warning'}>
              {viewModel.view.badge.label}
            </Typography>
          </Box>
        </Stack>

        {viewModel.view.warning && (
          <Alert variant="filled" color="warning">
            {viewModel.view.warning}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            overflow: 'hidden',
            borderRadius: '0.5rem',
            border: `1px solid ${grey[200]}`,
            gridTemplateColumns: 'repeat(2, 1fr)',
          }}
        >
          {viewModel.view.fields.map(field => (
            <Box key={field.label} sx={{ padding: '0.5rem', outline: `1px solid ${grey[200]}` }}>
              <Typography color="textSecondary">{field.label}</Typography>
              <Typography>{field.value || '-'}</Typography>
            </Box>
          ))}
        </Box>
      </Stack>

      <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" gap="0.5rem" alignItems="center">
          <Typography color="textDisabled" variant="body2" component="span">
            Last report: {moment(viewModel.createdDateTime).format(DateFormats.DateTimeMedium12Hour)}
          </Typography>

          <Typography color="textDisabled" variant="body2" component="span">
            Requested by: {viewModel.createdBy.fullName}
          </Typography>
        </Stack>

        <Stack flexDirection="row" alignItems="center" justifyContent="center" gap="1rem">
          {isSuperAdmin && (
            <GenerateMockReportBtn voId={voId} tooltip={generateReportTooltip} disabled={!isGenerateReportAvailable} />
          )}

          <GenerateReportBtn voId={voId} disabled={!isGenerateReportAvailable} tooltip={generateReportTooltip} />
        </Stack>
      </Stack>
    </Stack>
  );
};
